import { z } from "zod";
import { CompanySchema } from "../stages";

const TagSchema = z.object({
  id: z.number(),
  name: z.string()
});
export const CompanyProfileSchema = z.object({
  name: z.string(),
  logoUrl: z.string().nullable(),
  headline: z.string().nullable(),
  description: z.string().nullable(),
  website: z.string().nullable(),
  emailDomain: z.string().nullable(),
  foundingDate: z.coerce.date().nullable(),
  country: z.string().nullable(),
  state: z.string().nullable(),
  city: z.string().nullable(),
  financialStage: z.string().nullable(),

  facebookUrl: z.string().nullable(),
  linkedInUrl: z.string().nullable(),
  angelListUrl: z.string().nullable(),
  githubUrl: z.string().nullable(),
  intstagramUrl: z.string().nullable(),
  twitterUrl: z.string().nullable(),
  productHuntUrl: z.string().nullable(),
  headcount: z.coerce.number().nullable(),
  lastFundingAmount: z.coerce.bigint().nullable(),
  lastFundingDate: z.coerce.date().nullable(),
  fundingRoundsCount: z.coerce.number().nullable(),
  harmonicInvestors: z.array(
    z.object({
      name: z.string(),
      type: z.enum(["organization", "individual"]),
      imageUrl: z.string().nullable()
    })
  ),

  harmonicTags: z.array(z.string()).default([]),

  currencyId: z.coerce.number().nullable(),

  totalFunding: z.coerce.bigint().nullable(),
  valuation: z.coerce.bigint().nullable(),
  tam: z.bigint().nullable(),
  sam: z.bigint().nullable(),
  som: z.bigint().nullable(),
  tags: z.array(TagSchema)
});
export type TCompanyProfile = z.infer<typeof CompanyProfileSchema>;

export const HarmonicTeamMemberSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  title: z.string().nullable(),
  linkedInUrl: z.string().nullable(),
  profilePhotoUrl: z.string().nullable(),
  rank: z.number().min(0).max(6).optional()
});
export type THarmonicTeamMember = z.infer<typeof HarmonicTeamMemberSchema>;

export const HarmonicTeamMembersSchema = z.array(HarmonicTeamMemberSchema).default([]);
//similar companies

export const SimilarCompaniesSchema = z.object({
  status: z.enum(["processing", "done"]).nullable(),
  companies: z.array(CompanySchema).nullable(),
  similarCompaniesSearchStartedAt: z.coerce.date().nullish(),
  similarCompaniesSearchUpdatedAt: z.coerce.date().nullish()
});

export const SimilarCompaniesResponseSchema = SimilarCompaniesSchema.extend({
  meta: z.object({
    similarCompaniesSearchDescription: z.string().nullable()
  })
});

export type TSimilarCompaniesResponse = z.infer<typeof SimilarCompaniesResponseSchema>;
export type TSimilarCompany = z.infer<typeof CompanySchema>;

//market report

export const MarketReportSchema = z.object({
  status: z.enum(["processing", "done"]).nullable(),
  market: z
    .object({
      overview: z.string().default(""),
      keyFigures: z.array(
        z.object({
          name: z.string(),
          value: z.coerce.string(),
          unit: z.string().nullish(),
          comment: z.string().nullish()
        })
      )
    })
    .nullable(),
  source: z.string().nullable(),
  createdAt: z.coerce.date().nullish(),
  updatedAt: z.coerce.date().nullish()
});

export type TMarketReport = z.infer<typeof MarketReportSchema>;

// patents

export const PatentSchema = z.object({
  id: z.string(),
  title: z.string(),
  abstract: z.string(),
  date: z.coerce.date(),
  url: z.string()
});

export const PatentsSchema = z
  .object({
    totalCount: z.coerce.number().default(0),
    patents: z.array(PatentSchema).default([])
  })
  .default({ totalCount: 0, patents: [] }); // TODO -- figure out why it is sometimes undefined (e.g. when parsing Fintellect) - M

export type TPatent = z.infer<typeof PatentSchema>;
export type TPatents = z.infer<typeof PatentsSchema>;

// products

export const MetascraperProductSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  imageUrl: z.string().nullable(),
  description: z.string()
});

export type TProduct = z.infer<typeof MetascraperProductSchema>;

export const MetascraperProductsSchema = z.object({
  status: z.enum(["processing", "done"]).nullable(),
  summary: z.string().nullable(),
  products: z.array(MetascraperProductSchema).nullable(),
  urls: z.array(z.string()).nullable(),
  createdAt: z.coerce.date().nullish(),
  updatedAt: z.coerce.date().nullish()
});

export type TProducts = z.infer<typeof MetascraperProductsSchema>;
